import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { 
  parseCreditReport, 
  formatBureauName, 
  getFileTypeDisplay,
  getBureauTheme 
} from '../../services/creditReportService';
import './ParseCreditReport.css';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const ReportUploader = ({ bureau, onSuccess, clientUserId, compact = false }) => {
  const t = useTranslation();
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');

  const theme = getBureauTheme(bureau);

  const validateFile = (file) => {
    const allowedTypes = ['application/pdf'];

    if (!allowedTypes.includes(file.type)) {
      return t('creditReportParser.invalidFileType', 'Please upload a PDF file (.pdf)');
    }

    if (file.size > MAX_FILE_SIZE) {
      return t('creditReportParser.fileTooLarge', 'File size must be less than 20MB');
    }

    return null;
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setSelectedFile(file);
    }
  }, []);

  const handleFileSelect = (e) => {
    setError(null);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);
      setProgress(10);
      setProgressMessage(t('creditReportParser.validating', 'Validating file...'));

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 5;
        });
      }, 2000);

      setProgress(20);
      setProgressMessage(t('creditReportParser.analyzing', 'Analyzing credit report...'));

      const result = await parseCreditReport(selectedFile, clientUserId);

      clearInterval(progressInterval);
      setProgress(100);
      setProgressMessage(t('creditReportParser.complete', 'Complete!'));

      // Call success callback
      if (onSuccess) {
        onSuccess(result);
      }

      // Reset state
      setTimeout(() => {
        setSelectedFile(null);
        setProgress(0);
        setProgressMessage('');
      }, 1000);

    } catch (err) {
      console.error('Error parsing credit report:', err);
      setError(err.message || t('creditReportParser.parseError', 'Failed to parse credit report. Please try again.'));
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setError(null);
    setProgress(0);
    setProgressMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (compact) {
    return (
      <div className="uploader-compact">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".pdf,application/pdf"
          style={{ display: 'none' }}
        />
        
        {selectedFile ? (
          <div className="compact-selected">
            <span className="file-name">{selectedFile.name}</span>
            <button 
              className="compact-upload-btn"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? t('creditReportParser.processing', 'Processing...') : t('creditReportParser.parse', 'Parse')}
            </button>
            <button className="compact-clear-btn" onClick={handleClear}>×</button>
          </div>
        ) : (
          <button 
            className="compact-select-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            {t('creditReportParser.selectFile', 'Select New File')}
          </button>
        )}
        
        {error && <div className="compact-error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="report-uploader" style={{ '--bureau-gradient': theme.gradient }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".pdf,application/pdf"
        style={{ display: 'none' }}
      />

      {!selectedFile && !uploading ? (
        <div
          className={`drop-zone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="drop-zone-content">
            <div className="upload-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <h3>{`${t('creditReportParser.upload', 'Upload')} ${formatBureauName(bureau)} ${t('creditReportParser.report', 'Report')}`}</h3>
            <p className="drop-zone-subtitle">
              {t('creditReportParser.dropZoneSubtitle', 'Drag & drop your annual credit report here, or click to browse')}
            </p>
            <div className="file-types">
              <span className="file-type-badge">PDF</span>
            </div>
            <p className="file-size-note">
              {t('creditReportParser.maxFileSize', 'Maximum file size: 20MB')}
            </p>
          </div>
        </div>
      ) : null}

      {selectedFile && !uploading ? (
        <div className="file-preview">
          <div className="file-info">
            <div className="file-icon">
              {selectedFile.type === 'application/pdf' ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.5"/>
                  <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.5"/>
                  <text x="7" y="17" fontSize="5" fill="currentColor" fontWeight="bold">PDF</text>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.5"/>
                  <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.5"/>
                  <line x1="8" y1="13" x2="16" y2="13" stroke="currentColor" strokeWidth="1.5"/>
                  <line x1="8" y1="17" x2="14" y2="17" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              )}
            </div>
            <div className="file-details">
              <span className="file-name">{selectedFile.name}</span>
              <span className="file-meta">
                {getFileTypeDisplay(selectedFile.type)} • {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
          </div>
          
          <div className="file-actions">
            <button className="clear-btn" onClick={handleClear}>
              {t('creditReportParser.clear', 'Clear')}
            </button>
            <button 
              className="parse-btn"
              onClick={handleUpload}
              style={{ background: theme.gradient }}
            >
              <span className="btn-icon">🔍</span>
              {t('creditReportParser.parseReport', 'Parse Report')}
            </button>
          </div>
        </div>
      ) : null}

      {uploading && (
        <div className="upload-progress">
          <div className="progress-header">
            <span className="progress-icon">⚙️</span>
            <span className="progress-title">{progressMessage}</span>
          </div>
          <div className="progress-bar-container">
            <div 
              className="progress-bar" 
              style={{ 
                width: `${progress}%`,
                background: theme.gradient
              }}
            />
          </div>
          <div className="progress-details">
            <span className="progress-percentage">{progress}%</span>
            <span className="progress-step">
              {progress < 30 && t('creditReportParser.step1', 'Validating document...')}
              {progress >= 30 && progress < 70 && t('creditReportParser.step2', 'Extracting data with AI...')}
              {progress >= 70 && progress < 100 && t('creditReportParser.step3', 'Saving results...')}
              {progress >= 100 && t('creditReportParser.step4', 'Complete!')}
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="uploader-error">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button className="error-dismiss" onClick={() => setError(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default ReportUploader;

